import { Component, OnInit, computed, input, output, signal } from '@angular/core';

import { IconComponent } from '../../../shared/components/icon/icon.component';
import { formatDobNumeric } from '../../../shared/utils/date-time/date-time.utils';
import {
  MAX_SEARCH_MRN_LENGTH,
  MAX_SEARCH_NAME_LENGTH,
  MAX_SEARCH_SSN_LENGTH,
  MIN_SEARCH_FIELD_LENGTH,
  PatientSearchCriteria,
  hasEnoughCriteria,
  isSearchDateOfBirthInvalid,
  isSearchFieldTooShort,
  isSearchSsnInvalid,
} from '../../utils/patient-search.utils';

/**
 * The panel emits the same criteria shape the Search Patient Records screen
 * builds — see requests/utils/patient-search.utils.
 */
export type AdjustSearchCriteria = PatientSearchCriteria;

@Component({
  selector: 'app-adjust-search',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './adjust-search.html',
  styleUrl: './adjust-search.scss',
})
export class AdjustSearch implements OnInit {
  readonly firstName = input.required<string>();
  readonly lastName = input.required<string>();
  readonly dateOfBirth = input.required<string>();
  readonly lastFiveSsn = input<string | null>(null);
  readonly mrn = input<string | null>(null);
  readonly initialCriteria = input<AdjustSearchCriteria | null>(null);

  readonly cancelled = output<AdjustSearchCriteria>();
  readonly searchRun = output<AdjustSearchCriteria>();

  readonly firstNameValue = signal('');
  readonly lastNameValue = signal('');
  readonly dateOfBirthValue = signal('');
  readonly ssnValue = signal('');
  readonly mrnValue = signal('');

  // A DOB that isn't a real MM/DD/YYYY date blocks the search (with an inline
  // error) rather than being silently dropped from the criteria — a dropped
  // field would broaden the patient search beyond what the staff member asked
  // for (mirrors the Search Patient Records screen).
  readonly dateOfBirthInvalid = computed(() => isSearchDateOfBirthInvalid(this.dateOfBirthValue()));

  // A populated free-text criterion shorter than the minimum is held back for
  // the same reason as a malformed date: too broad to be half of the
  // two-criteria gate, and rejected by the API's own validator. An empty box
  // stays valid — that's a criterion left unset, not a bad value. MRN is on this
  // list because the server applies the identical minimum to it.
  readonly firstNameInvalid = computed(() => isSearchFieldTooShort(this.firstNameValue()));
  readonly lastNameInvalid = computed(() => isSearchFieldTooShort(this.lastNameValue()));
  readonly mrnInvalid = computed(() => isSearchFieldTooShort(this.mrnValue()));

  // `maxlength` keeps the SSN box to five characters, but nothing stops five
  // *non-digits* being typed or pasted in — the server rejects those.
  readonly ssnInvalid = computed(() => isSearchSsnInvalid(this.ssnValue()));

  readonly minFieldLength = MIN_SEARCH_FIELD_LENGTH;
  readonly maxNameLength = MAX_SEARCH_NAME_LENGTH;
  readonly maxMrnLength = MAX_SEARCH_MRN_LENGTH;
  readonly maxSsnLength = MAX_SEARCH_SSN_LENGTH;

  readonly canRunSearch = computed(
    () =>
      !this.dateOfBirthInvalid() &&
      !this.firstNameInvalid() &&
      !this.lastNameInvalid() &&
      !this.mrnInvalid() &&
      !this.ssnInvalid() &&
      hasEnoughCriteria(this.buildCurrentCriteria()),
  );

  /** The hint above the fields names the criteria the initial search used. */
  readonly searchHint =
    'The initial search used Name, DOB and MRN or SSN last 5. At least two fields must be used to run a search.';

  ngOnInit(): void {
    const criteria = this.initialCriteria();
    if (criteria) {
      this.firstNameValue.set(criteria.firstName ?? '');
      this.lastNameValue.set(criteria.lastName ?? '');
      this.dateOfBirthValue.set(criteria.dateOfBirth ?? '');
      this.ssnValue.set(criteria.lastFiveSsn ?? '');
      this.mrnValue.set(criteria.mrn ?? '');
    } else {
      this.resetToOriginalValues();
    }
  }

  clearSsn(): void {
    this.ssnValue.set('');
  }

  onCancel(): void {
    this.cancelled.emit(this.buildCurrentCriteria());
  }

  onResetToOriginal(): void {
    this.resetToOriginalValues();
  }

  onRunSearch(): void {
    if (!this.canRunSearch()) {
      return;
    }
    this.searchRun.emit(this.buildCurrentCriteria());
  }

  private buildCurrentCriteria(): AdjustSearchCriteria {
    return {
      firstName: this.firstNameValue().trim() || null,
      lastName: this.lastNameValue().trim() || null,
      dateOfBirth: this.dateOfBirthValue().trim() || null,
      lastFiveSsn: this.ssnValue().trim() || null,
      mrn: this.mrnValue().trim() || null,
    };
  }

  private resetToOriginalValues(): void {
    this.firstNameValue.set(this.firstName());
    this.lastNameValue.set(this.lastName());
    // formatDobNumeric is a pure string transform, so the date-only value
    // can't drift a day across a timezone boundary.
    this.dateOfBirthValue.set(formatDobNumeric(this.dateOfBirth()));
    this.ssnValue.set(this.lastFiveSsn() ?? '');
    this.mrnValue.set(this.mrn() ?? '');
  }
}
